import { useEffect, useState } from 'react';
import type { BookingBillStatus, BookingListItemDTO, Page, UpdateBookingRequest } from '@six-pockets/shared';
import { bookingsApi } from '@/api';
import { SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  ConfirmModal,
  DatePicker,
  InlineAlert,
  Pagination,
  SegmentedControl,
  SelectField,
  TextField,
  type SegmentOption,
  type SelectOption,
} from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useTableActions, useTablesData } from '@/context/TablesContext';
import { BookingFormModal } from '@/features/bookings/components/BookingFormModal';
import { BookingListRow } from '@/features/bookings/components/BookingListRow';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { todayKey } from '@/utils/date';
import { formatClock } from '@/utils/format';
import styles from '@/styles/pages/BookingsPage/style.scss';

const PAGE_SIZE = 12;
/** The table filter's "All tables" option (sends no tableId). */
const ALL_TABLES = 'all';

const STATUS_OPTIONS: SegmentOption<BookingBillStatus>[] = [
  { value: 'unbilled', label: 'Unbilled' },
  { value: 'billed', label: 'Billed' },
];

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/**
 * Every booking, billed or not, earliest start first. Filter by Unbilled / Billed, table and the day started (today by
 * default), search by name, and page through the results; the server does the filtering, search and paging.
 * Unbilled bookings can be edited, and deleted by the admin (the settings module): a soft delete, the booking leaves
 * the app but stays in the database.
 */
export function BookingsPage() {
  const { tables } = useTablesData();
  const { replaceTables } = useTableActions();
  const canDelete = useAuth().can('settings');

  const [billStatus, setBillStatus] = useState<BookingBillStatus>('unbilled');
  const [tableId, setTableId] = useState(ALL_TABLES);
  const [date, setDate] = useState(todayKey);
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<Page<BookingListItemDTO> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<BookingListItemDTO | null>(null);
  const [deleting, setDeleting] = useState<BookingListItemDTO | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // A new search goes back to the first page.
  useEffect(() => setPage(1), [query]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const table = tableId === ALL_TABLES ? undefined : tableId;
    bookingsApi.list({ billStatus, tableId: table, date, q: query || undefined, page, pageSize: PAGE_SIZE }, ctrl.signal).then(
      (r) => {
        if (r.items.length === 0 && r.page > 1) setPage(r.page - 1);
        else setResult(r);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [billStatus, tableId, date, query, page, reloadKey]);

  /** Saves the edit, keeps the table cards in step, and reloads this page (a completed booking comes back re-priced). */
  const save = async (body: UpdateBookingRequest) => {
    if (!editing) return;
    setBusy(true);
    setModalError(null);
    try {
      replaceTables([await bookingsApi.update(editing.id, body)]);
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  /** Deletes the booking, takes it off its table card, and reloads this page. */
  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setModalError(null);
    try {
      replaceTables([await bookingsApi.remove(deleting.id)]);
      setDeleting(null);
      setReloadKey((k) => k + 1);
    } catch (err) {
      setModalError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setEditing(null);
    setDeleting(null);
    setModalError(null);
  };

  const changeStatus = (s: BookingBillStatus) => {
    setBillStatus(s);
    setPage(1);
  };
  const changeDate = (d: string) => {
    setDate(d);
    setPage(1);
  };
  const changeTable = (id: string) => {
    setTableId(id);
    setPage(1);
  };

  const tableOptions: SelectOption[] = [{ value: ALL_TABLES, label: 'All tables' }, ...tables.map((t) => ({ value: t.id, label: t.name }))];

  const bookings = result?.items ?? [];

  return (
    <>
      <PageHeader title="Bookings" />

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <SegmentedControl legend="Show bookings" hideLegend columns={2} options={STATUS_OPTIONS} value={billStatus} onChange={changeStatus} />
        </div>
        <div className={styles.tools}>
          <SelectField size="md" aria-label="Table" options={tableOptions} value={tableId} onChange={changeTable} className={styles.tableFilter} />
          <TextField
            size="md"
            aria-label="Search bookings by name"
            placeholder="Search bookings"
            prefix={<SearchIcon size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
          <div className={styles.date}>
            <DatePicker label="Day started" value={date} onChange={changeDate} />
          </div>
        </div>
      </div>

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading bookings…</p>
      ) : bookings.length === 0 ? (
        <p className={styles.empty}>{billStatus === 'unbilled' ? 'No unbilled bookings' : 'No billed bookings'}</p>
      ) : (
        <div className={styles.listWrap}>
          <div className={styles.columns} aria-hidden>
            <span />
            <span>Name</span>
            <span>Table</span>
            <span>Start</span>
            <span>End</span>
            <span>Duration</span>
            <span className={styles.right}>Amount</span>
            <span />
          </div>
          <ul className={styles.list} aria-busy={loading}>
            {bookings.map((b) => (
              <BookingListRow
                key={b.id}
                booking={b}
                onEdit={b.billStatus === 'unbilled' ? () => setEditing(b) : undefined}
                onDelete={canDelete && b.billStatus === 'unbilled' ? () => setDeleting(b) : undefined}
              />
            ))}
          </ul>
        </div>
      )}

      {result && <Pagination page={result.page} pageSize={result.pageSize} total={result.total} onPageChange={setPage} />}

      <ConfirmModal
        open={!!deleting}
        title="Delete booking"
        message={
          deleting && (
            <>
              Are you sure you want to delete <strong>{deleting.customerName || 'the unnamed booking'}</strong> on{' '}
              <strong>{deleting.tableName}</strong> ({formatClock(deleting.startedAt)}
              {deleting.endedAt ? ` – ${formatClock(deleting.endedAt)}` : ', still in play'})?
            </>
          )
        }
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={busy}
        error={modalError}
        onConfirm={() => void remove()}
        onCancel={closeModal}
      />

      {editing && (
        <BookingFormModal key={editing.id} booking={editing} busy={busy} error={modalError} onSave={(body) => void save(body)} onClose={closeModal} />
      )}
    </>
  );
}
